const VOICE_MUTE_STORAGE_KEY = "intentico:tutorialVoiceMuted";
const VOICE_RATE_STORAGE_KEY = "intentico:tutorialSpeechRate";
const SPEECH_END_BEAT_MS = 600;
/** Softened base engine rate; user playback multiplies on top. */
const BASE_UTTERANCE_RATE = 0.92;

/** Post-speech pause before auto-play advances. */
export const TUTORIAL_SPEECH_END_BEAT_MS = SPEECH_END_BEAT_MS;

/** YouTube-style narration playback speeds. */
export const TUTORIAL_SPEECH_RATES = [0.75, 1, 1.25, 1.5] as const;
export type TutorialSpeechRate = (typeof TUTORIAL_SPEECH_RATES)[number];

type SpeakOptions = {
  /** User-facing playback multiplier (1 = normal). */
  rate?: TutorialSpeechRate;
  onStart?: () => void;
  onEnd?: () => void;
};

export function isTutorialSpeechRate(value: number): value is TutorialSpeechRate {
  return (TUTORIAL_SPEECH_RATES as readonly number[]).includes(value);
}

export function formatTutorialSpeechRateLabel(rate: TutorialSpeechRate): string {
  return rate === 1 ? "1x" : `${rate}x`;
}

export function readTutorialSpeechRatePreference(): TutorialSpeechRate {
  try {
    const raw = Number(sessionStorage.getItem(VOICE_RATE_STORAGE_KEY));
    if (isTutorialSpeechRate(raw)) return raw;
  } catch {
    // sessionStorage may be unavailable
  }
  return 1;
}

export function writeTutorialSpeechRatePreference(rate: TutorialSpeechRate): void {
  try {
    sessionStorage.setItem(VOICE_RATE_STORAGE_KEY, String(rate));
  } catch {
    // sessionStorage may be unavailable
  }
}

function clampEngineRate(rate: number): number {
  return Math.min(2, Math.max(0.5, rate));
}

let activeUtterance: SpeechSynthesisUtterance | null = null;
let preferredVoice: SpeechSynthesisVoice | null = null;
let voicesLoaded = false;
let speakGeneration = 0;

/** Preferred free neural voices by name substring (best first). */
const PREFERRED_VOICE_HINTS = [
  "aria online",
  "jenny online",
  "guy online",
  "sara online",
  "michelle online",
  "andrew online",
  "emma online",
  "brian online",
  "natural",
  "neural",
  "google us english",
  "google uk english female",
  "google uk english male",
  "samantha",
  "karen",
  "moira",
  "ava",
  "zoe",
  "nicky",
  "susan",
] as const;

const ROBOTIC_VOICE_HINTS = [
  "desktop",
  "zira",
  "david",
  "mark",
  "hazel",
  "compact",
  "eloquence",
  "espeak",
  "microsoft david",
  "microsoft zira",
  "microsoft mark",
  "microsoft hazel",
] as const;

export function isTutorialSpeechSupported(): boolean {
  return typeof window !== "undefined" && "speechSynthesis" in window;
}

export function readTutorialVoiceMutedPreference(): boolean {
  try {
    return sessionStorage.getItem(VOICE_MUTE_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export function writeTutorialVoiceMutedPreference(muted: boolean): void {
  try {
    sessionStorage.setItem(VOICE_MUTE_STORAGE_KEY, muted ? "1" : "0");
  } catch {
    // sessionStorage may be unavailable
  }
}

function scoreVoice(voice: SpeechSynthesisVoice): number {
  const name = voice.name.toLowerCase();
  const lang = voice.lang.toLowerCase();
  let score = 0;

  if (!lang.startsWith("en")) return -1000;
  if (lang === "en-us" || lang === "en_us") score += 12;
  else if (lang.startsWith("en-gb") || lang.startsWith("en_gb")) score += 6;

  for (let i = 0; i < PREFERRED_VOICE_HINTS.length; i++) {
    if (name.includes(PREFERRED_VOICE_HINTS[i])) {
      score += 120 - i * 3;
      break;
    }
  }

  if (name.includes("online")) score += 55;
  if (name.includes("natural") || name.includes("neural")) score += 50;
  if (name.includes("microsoft") && name.includes("online")) score += 40;
  if (name.includes("google") && !name.includes("español")) score += 35;

  // Cloud / neural voices are almost always localService === false.
  // Windows SAPI desktop voices are local and sound much more robotic.
  if (!voice.localService) score += 45;
  else score -= 35;

  for (const hint of ROBOTIC_VOICE_HINTS) {
    if (name.includes(hint) && !name.includes("online") && !name.includes("natural")) {
      score -= 80;
      break;
    }
  }

  return score;
}

function pickPreferredVoice(): SpeechSynthesisVoice | null {
  if (!isTutorialSpeechSupported()) return null;
  const voices = window.speechSynthesis.getVoices();
  if (!voices.length) return null;
  const ranked = [...voices]
    .filter((voice) => voice.lang.toLowerCase().startsWith("en"))
    .sort((a, b) => scoreVoice(b) - scoreVoice(a));
  return ranked[0] ?? null;
}

function ensureVoicesLoaded(): Promise<SpeechSynthesisVoice | null> {
  if (!isTutorialSpeechSupported()) return Promise.resolve(null);

  const existing = window.speechSynthesis.getVoices();
  if (existing.length) {
    preferredVoice = pickPreferredVoice();
    voicesLoaded = true;
    return Promise.resolve(preferredVoice);
  }

  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      preferredVoice = pickPreferredVoice();
      voicesLoaded = true;
      resolve(preferredVoice);
    };

    window.speechSynthesis.addEventListener("voiceschanged", finish, { once: true });
    window.setTimeout(finish, 500);
  });
}

/** Rough spoken duration for ETA labels when utterance.onend isn't available yet. */
export function estimateSpeechDurationMs(
  text: string,
  playbackRate: TutorialSpeechRate = 1,
): number {
  const trimmed = text.trim();
  if (!trimmed) return 0;
  const words = trimmed.split(/\s+/).length;
  // ~145 wpm presenter pace with a little breathing room + sentence beats.
  const sentences = Math.max(1, (trimmed.match(/[.!?]+/g) ?? []).length);
  const fromWords = Math.round((words / 145) * 60_000);
  const atNormal = Math.min(
    32_000,
    Math.max(5_500, fromWords + 800 + sentences * 180),
  );
  const rate = Math.max(0.5, playbackRate);
  return Math.round(atNormal / rate);
}

export function cancelTutorialSpeech(): void {
  speakGeneration += 1;
  if (!isTutorialSpeechSupported()) return;
  activeUtterance = null;
  window.speechSynthesis.cancel();
}

/** Split into short clauses so prosody breathes between thoughts. */
function splitNarrationChunks(text: string): string[] {
  const normalized = text.replace(/\s+/g, " ").trim();
  if (!normalized) return [];

  const parts = normalized
    .split(/(?<=[.!?])\s+|(?<=;)\s+|(?<=—)\s+|(?<=–)\s+/)
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length <= 1) return [normalized];

  const chunks: string[] = [];
  let buffer = "";
  for (const part of parts) {
    const next = buffer ? `${buffer} ${part}` : part;
    // Keep chunks short — long Chrome utterances can chop/robotize.
    if (next.length > 140 && buffer) {
      chunks.push(buffer);
      buffer = part;
    } else {
      buffer = next;
    }
  }
  if (buffer) chunks.push(buffer);
  return chunks;
}

function speakChunk(
  text: string,
  voice: SpeechSynthesisVoice | null,
  generation: number,
  playbackRate: TutorialSpeechRate,
): Promise<void> {
  return new Promise((resolve) => {
    if (generation !== speakGeneration || !isTutorialSpeechSupported()) {
      resolve();
      return;
    }

    const utterance = new SpeechSynthesisUtterance(text);
    if (voice) {
      utterance.voice = voice;
      utterance.lang = voice.lang || "en-US";
    } else {
      utterance.lang = "en-US";
    }
    // Slightly slower base + tiny pitch warmth, then user playback multiplier.
    utterance.rate = clampEngineRate(BASE_UTTERANCE_RATE * playbackRate);
    utterance.pitch = 1.02;
    utterance.volume = 1;

    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      if (activeUtterance === utterance) activeUtterance = null;
      resolve();
    };

    utterance.onend = finish;
    utterance.onerror = finish;
    activeUtterance = utterance;
    window.speechSynthesis.speak(utterance);
  });
}

/**
 * Speak a tour narration script. Cancels any in-flight utterance first.
 * Returns false if speech isn't available or text is empty.
 */
export function speakTutorialNarration(
  text: string,
  options: SpeakOptions = {},
): boolean {
  const trimmed = text.trim();
  if (!trimmed || !isTutorialSpeechSupported()) {
    options.onEnd?.();
    return false;
  }

  const playbackRate = options.rate ?? 1;
  cancelTutorialSpeech();
  const generation = speakGeneration;
  const chunks = splitNarrationChunks(trimmed);
  // Shorter gaps when sped up so autoplay still feels tight.
  const chunkGapMs = Math.max(80, Math.round(220 / playbackRate));

  void (async () => {
    const voice = (await ensureVoicesLoaded()) ?? pickPreferredVoice();
    if (generation !== speakGeneration) return;

    preferredVoice = voice;
    options.onStart?.();

    for (let i = 0; i < chunks.length; i++) {
      if (generation !== speakGeneration) return;
      await speakChunk(chunks[i], voice, generation, playbackRate);
      // Brief pause between sentences — free TTS has no SSML, so fake rhythm.
      if (i < chunks.length - 1 && generation === speakGeneration) {
        await new Promise((resolve) => window.setTimeout(resolve, chunkGapMs));
      }
    }

    if (generation === speakGeneration) {
      options.onEnd?.();
    }
  })();

  return true;
}
